import React, { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import PageLayout from "@/components/layout/PageLayout";
import PageHero from "@/components/PageHero";
import TourCard from "@/components/TourCard";
import Seo from "@/components/Seo";
import { breadcrumbSchema, itemListSchema } from "@/components/JsonLd";
import { useSiteContent } from "@/context/SiteContentContext";
import { Globe2, Clock, DollarSign, Activity, X, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import SearchSuggestionsList from "@/components/SearchSuggestionsList";
import { useTourSuggestions } from "@/hooks/useTourSuggestions";
import { useRecentSearches } from "@/hooks/useRecentSearches";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const COUNTRIES = ["any", "India", "Nepal", "Bhutan", "Sri Lanka", "Maldives", "Thailand", "UAE"];
const DURATIONS = [
  { v: "any", l: "Any length" }, { v: "1-4", l: "1-4 days" }, { v: "5-7", l: "5-7 days" },
  { v: "8-12", l: "8-12 days" }, { v: "13+", l: "13+ days" },
];
const BUDGETS = [
  { v: "any", l: "Any budget" }, { v: "0-500", l: "Under $500" },
  { v: "500-1000", l: "$500–1,000" }, { v: "1000-2000", l: "$1,000–2,000" }, { v: "2000+", l: "$2,000+" },
];
const DIFFICULTIES = ["any", "Easy", "Moderate", "Challenging"];

const CATEGORIES = [
  { value: "all", label: "All Journeys" },
  { value: "golden-triangle", label: "Golden Triangle" },
  { value: "classical-india", label: "Classical India" },
  { value: "rajasthan", label: "Rajasthan" },
  { value: "south-india", label: "South India" },
  { value: "spiritual", label: "Spiritual" },
  { value: "yoga-wellness", label: "Yoga & Wellness" },
  { value: "diwali", label: "Diwali" },
  { value: "holi", label: "Holi" },
  { value: "wildlife", label: "Wildlife" },
  { value: "family", label: "Family" },
  { value: "honeymoon", label: "Honeymoon" },
  { value: "adventure", label: "Adventure" },
  { value: "kashmir", label: "Kashmir" },
  { value: "ladakh", label: "Ladakh" },
  { value: "same-day", label: "Same Day" },
  { value: "nepal", label: "Nepal" },
  { value: "bhutan", label: "Bhutan" },
  { value: "sri-lanka", label: "Sri Lanka" },
  { value: "maldives", label: "Maldives" },
  { value: "dubai", label: "Dubai" },
  { value: "thailand", label: "Thailand" },
];

function inRange(value, range) {
  if (range === "any") return true;
  if (range.endsWith("+")) {
    const min = parseInt(range, 10);
    return value >= min;
  }
  const [a, b] = range.split("-").map((x) => parseInt(x, 10));
  return value >= a && value <= b;
}

export default function Tours() {
  const c = useSiteContent("tours");
  const [params, setParams] = useSearchParams();
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);

  const category = params.get("category") || "all";
  const country = params.get("country") || "any";
  const duration = params.get("duration") || "any";
  const budget = params.get("budget") || "any";
  const difficulty = params.get("difficulty") || "any";
  const q = params.get("q") || "";
  const [searchInput, setSearchInput] = useState(q);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const liveSuggestions = useTourSuggestions(searchInput);
  const { recent: recentSearches, addSearch, removeSearch } = useRecentSearches();

  useEffect(() => setSearchInput(q), [q]);

  useEffect(() => {
    setLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/tours");
        setTours(data);
      } catch (e) {
        console.warn("[Tours] failed to load tour list:", e?.message || e);
      } finally {
        setLoading(false);
      }
    })();
    // `api` is a module singleton and useState setters are stable — safe empty deps.
  }, []);

  const setParam = (key, val) => {
    const next = new URLSearchParams(params);
    if (val === "all" || val === "any" || val === "") next.delete(key);
    else next.set(key, val);
    setParams(next);
  };

  const submitSearch = (e) => {
    e.preventDefault();
    addSearch(searchInput);
    setParam("q", searchInput);
  };

  const filtered = useMemo(() => {
    const keyword = q.trim().toLowerCase();
    return tours.filter((t) => {
      if (category !== "all" && t.category !== category) return false;
      if (country !== "any" && t.country !== country) return false;
      if (difficulty !== "any" && t.difficulty_level !== difficulty) return false;
      if (duration !== "any" && !inRange(t.days || 0, duration)) return false;
      if (budget !== "any" && !inRange(t.price_usd || 0, budget)) return false;
      if (keyword) {
        const haystack = [
          t.title, t.summary, t.description, t.category, t.country, t.region,
          ...(t.locations || []), ...(t.highlights || []),
        ].filter(Boolean).join(" ").toLowerCase();
        if (!haystack.includes(keyword)) return false;
      }
      return true;
    });
  }, [tours, category, country, duration, budget, difficulty, q]);

  const activeFilterCount = [country, duration, budget, difficulty].filter((x) => x !== "any").length + (q ? 1 : 0);

  const clearAll = () => {
    const next = new URLSearchParams();
    if (category !== "all") next.set("category", category);
    setParams(next);
    setSearchInput("");
  };

  return (
    <PageLayout>
      <Seo
        title="All Tours — India, Nepal, Dubai & Thailand"
        description="Browse all curated tours: Golden Triangle, Rajasthan Heritage, Spiritual India, Wildlife Safaris, Festival Specials and extensions to Nepal, Dubai and Thailand. Filter by country, duration & budget."
        jsonLd={[
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Tours", path: "/tours" },
          ]),
          itemListSchema({
            name: "All Tours — Tour Trips India Holidays",
            items: tours.map((t) => ({ name: t.title, url: `/tours/${t.slug}` })),
          }),
        ]}
      />
      <PageHero
        eyebrow={c.hero_eyebrow}
        title={c.hero_title}
        subtitle={c.hero_subtitle}
        image={c.hero_image}
      />

      <section className="py-10 bg-cream-100 border-b border-cream-200" data-testid="tours-filter-bar">
        <div className="container-tti">
          <form onSubmit={submitSearch} className="relative flex items-center gap-2 bg-white border border-cream-200 rounded-sm mb-4 max-w-lg" data-testid="tours-search-form">
            <Search className="w-4 h-4 text-saffron ml-3 flex-shrink-0" />
            <Input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setShowSuggestions(false)}
              placeholder="Search by keyword — e.g. Kerala, Wildlife, Taj Mahal…"
              data-testid="tours-search-input"
              className="border-0 shadow-none focus-visible:ring-0 bg-transparent h-11"
            />
            <button type="submit" data-testid="tours-search-submit" className="px-5 h-11 text-xs uppercase tracking-wider font-medium text-white bg-saffron hover:bg-saffron-600 transition-colors flex-shrink-0">
              Search
            </button>
            {showSuggestions && (
              <SearchSuggestionsList
                query={searchInput}
                suggestions={liveSuggestions}
                recent={recentSearches}
                testidPrefix="tours-suggestion"
                onSelect={() => setSearchInput("")}
                onSelectRecent={(q) => { setSearchInput(q); addSearch(q); setParam("q", q); }}
                onRemoveRecent={removeSearch}
              />
            )}
          </form>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <FilterSelect icon={Globe2} label="Country" value={country} testid="filter-country" onChange={(v) => setParam("country", v)}>
              {COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c === "any" ? "Any country" : c}</SelectItem>)}
            </FilterSelect>
            <FilterSelect icon={Clock} label="Duration" value={duration} testid="filter-duration" onChange={(v) => setParam("duration", v)}>
              {DURATIONS.map((d) => <SelectItem key={d.v} value={d.v}>{d.l}</SelectItem>)}
            </FilterSelect>
            <FilterSelect icon={DollarSign} label="Budget" value={budget} testid="filter-budget" onChange={(v) => setParam("budget", v)}>
              {BUDGETS.map((b) => <SelectItem key={b.v} value={b.v}>{b.l}</SelectItem>)}
            </FilterSelect>
            <FilterSelect icon={Activity} label="Difficulty" value={difficulty} testid="filter-difficulty" onChange={(v) => setParam("difficulty", v)}>
              {DIFFICULTIES.map((d) => <SelectItem key={d} value={d}>{d === "any" ? "Any difficulty" : d}</SelectItem>)}
            </FilterSelect>
          </div>

          <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1">
            {CATEGORIES.map((c) => {
              const isActive = category === c.value;
              return (
                <button
                  key={c.value}
                  data-testid={`filter-${c.value}`}
                  onClick={() => setParam("category", c.value === "all" ? "all" : c.value)}
                  className={`px-4 py-2 rounded-sm text-xs uppercase tracking-[0.15em] font-medium whitespace-nowrap transition-all border ${
                    isActive ? "bg-deepred text-white border-deepred" : "bg-white text-foreground border-cream-200 hover:border-saffron hover:text-saffron"
                  }`}
                >
                  {c.label}
                </button>
              );
            })}
          </div>

          {activeFilterCount > 0 && (
            <button onClick={clearAll} data-testid="clear-filters" className="mt-3 text-xs text-saffron hover:underline inline-flex items-center gap-1">
              <X className="w-3 h-3" /> Clear {activeFilterCount} filter{activeFilterCount > 1 ? "s" : ""}
            </button>
          )}
        </div>
      </section>

      <section className="py-16 pb-32" data-testid="tours-grid">
        <div className="container-tti">
          <div className="mb-6 text-sm text-foreground/65">{filtered.length} of {tours.length} tours match your search</div>
          {loading ? (
            <div className="text-center py-20 text-foreground/60">Loading journeys…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-20">
              <div className="font-serif text-3xl text-foreground/70 mb-3">No tours match these filters.</div>
              <button onClick={clearAll} className="btn-primary">Clear filters</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((t) => <TourCard key={t.slug} tour={t} />)}
            </div>
          )}
        </div>
      </section>
    </PageLayout>
  );
}

function FilterSelect({ icon: Icon, label, value, onChange, testid, children }) {
  return (
    <div className="bg-white border border-cream-200 rounded-sm flex items-center gap-2 px-3">
      <Icon className="w-4 h-4 text-saffron flex-shrink-0" />
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testid} className="border-0 bg-transparent shadow-none h-11 px-0 focus:ring-0 text-sm">
          <SelectValue placeholder={label} />
        </SelectTrigger>
        <SelectContent>{children}</SelectContent>
      </Select>
    </div>
  );
}
